import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnDestroy, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { ButtonComponent } from "src/app/@core/components/ui/button/button.component";
import { DialogComponent } from "src/app/@core/components/ui/dialog/dialog.component";
import { IconComponent } from "src/app/@core/components/ui/icon/icon.component";
import { PopoverComponent } from 'src/app/@core/components/ui/popover/popover.component';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { GenerationLoaderComponent } from 'src/app/@shared/components/generation-loader/generation-loader.component';
import { LottieAnimationComponent } from "src/app/@shared/components/lottie-animation/lottie-animation.component";
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { DynamicComponentRenderer } from 'src/app/@shared/directives/dynamic-component-renderer.directive';
import { GEOTECHNICAL_ANALYSIS_SVG, SVG } from 'src/app/common/svgs';
import { FormatTextPipe } from "../../../../../../@shared/pipes/formated-text.pipe";
import { GEOTECHNICAL_SUMMARY_STATUS, GeotechnicalAnalysisService, GeotechnicalAnalysisSummaryListItem } from 'src/app/features/geotechnical-analysis/services/geotechnical-analysis.service';
import { GeoTechnicalAnalysisReportDataService } from 'src/app/features/geotechnical-analysis/services/geo-technical-analysis-report-data.service';
@Component({
  selector: 'app-geo-technical-summary',
  imports: [LottieAnimationComponent, FormatTextPipe, IconComponent, ButtonComponent, CommonModule, FormsModule, TooltipModule, UserNameDisplayComponent, PopoverComponent, DialogComponent, DynamicComponentRenderer, GenerationLoaderComponent],
  templateUrl: './geo-technical-summary.component.html',
  styleUrl: './geo-technical-summary.component.scss'
})
export class GeoTechnicalSummaryComponent implements OnChanges, OnDestroy {

  @Input() analysisId = '';
  @Input() summaryDescriptionInput = '';

  generationAnimationData: any;
  /** Animation data for the "In Queue" waiting state (loading-spinner Lottie) */
  queueAnimationData: any;

  GEOTECHNICAL_ANALYSIS_SVG = GEOTECHNICAL_ANALYSIS_SVG
  SVG = SVG

  summaryDescriptionDialogVisible = false;

  /**
   * Last per-request instructions used for this analysis (from the backend).
   * Pre-fills the regen dialog when the user opens it.
   */
  lastUserInstructions: string | null = null;

  /**
   * Workspace-level Summary Instructions (from the configuration drawer).
   * Used as a fallback pre-fill when no per-analysis instructions exist.
   */
  workspaceInstructions: string | null = null;

  /**
   * True when the dialog textarea is pre-filled from workspace instructions.
   * Drives the "workspace" badge shown in the dialog.
   */
  instructionsFromWorkspace = false;

  loading = false;

  /** Current generation status returned by the backend (null until first fetch resolves) */
  summaryStatus: GEOTECHNICAL_SUMMARY_STATUS | null = null;

  /** Expose the enum to the template for direct comparison */
  readonly GEOTECHNICAL_SUMMARY_STATUS = GEOTECHNICAL_SUMMARY_STATUS;

  latestSummaryDetails: {
    _id: string,
    summary: string,
  } = {
      _id: '',
      summary: ''
    }

  openHistory = false;

  summaryHistoryList: GeotechnicalAnalysisSummaryListItem[] = [];
  loadingHistory = false;
  downloadLoading = signal<boolean>(false)

  private geotechnicalAnalysisService = inject(GeotechnicalAnalysisService);
  private geoReportDataService = inject(GeoTechnicalAnalysisReportDataService);
  private toast = inject(ToastService);

  get isViewer(): boolean {
    const p = this.geoReportDataService.permissions();
    return !this.geoReportDataService.isOwner() && !this.geoReportDataService.isEditor() && !p?.canComment;
  }
  /** Single subscription covering all summary status transitions (replaces separate completed/failed subs) */
  private summaryStatusSubscription: Subscription;

  constructor(private socketService: SocketService) {
    /**
     * Listen for GEOTECHNICAL_SUMMARY_STATUS_CHANGED events.
     * We guard against stale events from other analyses by comparing the
     * incoming geotechnicalAnalysisId with the current analysisId input.
     */
    this.summaryStatusSubscription = this.socketService
      .on<{ geotechnicalAnalysisId: string; status: GEOTECHNICAL_SUMMARY_STATUS }>(
        SOCKET_EVENTS.GEOTECHNICAL_SUMMARY_STATUS_CHANGED
      )
      .subscribe(result => {
        const payload = result?.[0];
        if (!payload || payload.geotechnicalAnalysisId !== this.analysisId) {
          // Event belongs to a different analysis — ignore it
          return;
        }

        // Mirror every status transition into the component so the template reacts immediately
        this.summaryStatus = payload.status;

        if (payload.status === GEOTECHNICAL_SUMMARY_STATUS.COMPLETED) {
          // Re-fetch the full summary from the backend now that generation finished
          this.fetchLatestSummary();
        } else if (payload.status === GEOTECHNICAL_SUMMARY_STATUS.FAILED) {
          this.loading = false;
          this.toast.error('Summary generation failed');
        }
      });
  }

  ngOnInit(): void {
    this.loadGenerationAnimation();
    this.loadQueueAnimation();
    if (this.analysisId) {
      this.fetchLatestSummary();
    }
  }

  ngOnDestroy(): void {
    // Clean up the unified status subscription to prevent memory leaks
    if (this.summaryStatusSubscription) {
      this.summaryStatusSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['analysisId'] && this.analysisId) {
      this.fetchLatestSummary();
    }
  }

  fetchLatestSummary(): void {
    this.loading = true;
    this.geotechnicalAnalysisService.fetchLatestSummary(this.analysisId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.latestSummaryDetails._id = res.data._id ?? '';
          this.latestSummaryDetails.summary = res.data.summary ?? '';
          this.summaryStatus = res.data.summaryStatus ?? null;
          this.lastUserInstructions = res.data.lastUserInstructions ?? null;
          this.workspaceInstructions = res.data.workspaceInstructions ?? null;
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /** Opens the re-generate dialog, pre-filling from last-used or workspace instructions (mirrors AI Dashboard). */
  onRegenerate(): void {
    if (this.lastUserInstructions) {
      this.summaryDescriptionInput = this.lastUserInstructions;
      this.instructionsFromWorkspace = false;
    } else if (this.workspaceInstructions) {
      this.summaryDescriptionInput = this.workspaceInstructions;
      this.instructionsFromWorkspace = true;
    } else {
      this.summaryDescriptionInput = '';
      this.instructionsFromWorkspace = false;
    }
    this.summaryDescriptionDialogVisible = true;
  }

  onSubmitSummaryDescription(): void {
    if (!this.analysisId) return;
    this.loading = true;
    this.summaryDescriptionDialogVisible = false;
    this.geotechnicalAnalysisService.reGenerateSummary(this.analysisId, this.summaryDescriptionInput).subscribe({
      next: (res) => {
        if (res.success) {
          this.toast.success(res.message ?? 'Summary re-generated');
        } else {
          this.toast.error(res.message ?? 'Failed to re-generate summary');
          this.loading = false;
        }
      },
      error: () => {
        this.loading = false;
        this.toast.error('Failed to re-generate summary');
      },
    });
  }

  onHistoryClick(): void {
    if (!this.analysisId) return;
    this.loadingHistory = true;
    this.openHistory = true;
    this.geotechnicalAnalysisService.summaryList(this.analysisId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.summaryHistoryList = Array.isArray(res.data) ? res.data : [];
        } else {
          this.summaryHistoryList = [];
        }
        this.loadingHistory = false;
      },
      error: () => {
        this.summaryHistoryList = [];
        this.loadingHistory = false;
      },
    });
  }

  onDownloadSummaryItem(summaryId: string | undefined): void {
    if (!summaryId) return
    this.toast.info("Download in progress…")
    this.downloadLoading.set(true)
    this.geotechnicalAnalysisService.downloadSummaryById(summaryId).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.toast.error('Failed to download summary');
          return;
        }
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'summary.pdf';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
            ?? contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
          if (match?.[1]) {
            filename = decodeURIComponent(match[1].trim());
          }
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.downloadLoading.set(false)
        this.toast.success('Summary downloaded successfully');
      },
      error: () => {
        this.downloadLoading.set(false)
        this.toast.error('Failed to download summary');
      },
    });
  }

  onDeleteSummaryItem(item: GeotechnicalAnalysisSummaryListItem): void {
    const summaryId = item._id;
    if (!summaryId) return;
    this.geotechnicalAnalysisService.deleteSummary(summaryId).subscribe({
      next: (res) => {
        if (res.success) {
          this.toast.success('Summary deleted');
          this.summaryHistoryList = this.summaryHistoryList.filter((i) => i._id !== summaryId);
        } else {
          this.toast.error(res.message ?? 'Failed to delete summary');
        }
      },
      error: () => {
        this.toast.error('Failed to delete summary');
      },
    });
  }

  loadQueueAnimation() {
    // Load the loading-spinner Lottie animation for the "In Queue" state
    fetch('/animations/loading-spinner.json')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        this.queueAnimationData = data;
      })
      .catch(error => {
        console.error('Error loading queue spinner animation:', error);
      });
  }

  loadGenerationAnimation() {
    // Load the template generation animation data from file
    fetch('/animations/template_generation.json')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        this.generationAnimationData = data;
      })
      .catch(error => {
        console.error('Error loading template generation animation:', error);
      });
  }

}
